import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RatesFilterStateService } from '../../state/rates-filter-state.service';
import { POPOVER_CONTEXT, PickerComponent } from './picker.types';
import { pickerRegistry } from './picker-registry';
import { StateAvailabilityGridComponent } from '../../../../shared/state-availability/state-availability-grid.component';

/**
 * /rates' State Availability picker.
 *
 * Now a thin adapter over the shared StateAvailabilityGridComponent, which
 * carries the 50-state grid, the search and the styles this component used to
 * own. /products and /changes render the same control, so the three pages
 * cannot drift apart visually — the grid moved to shared/ for exactly that
 * reason.
 *
 * What stays here is the /rates wiring: the picker-registry entry, and reading
 * and writing the active tab's filter slot. Live-apply per D9 — every cell
 * click writes through `setSlotField` and triggers `requestApplyFilters()`.
 */
@Component({
  selector: 'app-state-grid-picker',
  standalone: true,
  imports: [CommonModule, StateAvailabilityGridComponent],
  template: `
    <app-state-availability-grid
      [selected]="selected()"
      (stateToggle)="toggle($event)" />
  `,
  styles: [`:host { display: contents; }`],
})
export class StateGridPickerComponent implements PickerComponent {
  /**
   * Optional, because this component now has two render paths. Through
   * InlinePickerHostComponent it gets a POPOVER_CONTEXT from the host's
   * sub-injector, as every registry picker does. The rail also renders it
   * DIRECTLY as the State Availability group's body — that group holds one
   * dimension, so the picker row between the group and the grid was a second
   * accordion that only ever opened one thing. Nothing here reads the context,
   * so the direct path simply has none rather than faking one.
   */
  protected readonly ctx = inject(POPOVER_CONTEXT, { optional: true });
  protected readonly filterState = inject(RatesFilterStateService);

  /** Codes currently selected on the active slot. */
  protected readonly selected = computed<string[]>(() => {
    this.filterState.version();
    const v = this.filterState.activeSlot().selectedStates;
    return Array.isArray(v) ? v : [];
  });

  toggle(code: string): void {
    const current = this.selected();
    const next = current.includes(code)
      ? current.filter((v) => v !== code)
      : [...current, code];
    this.filterState.setSlotField('selectedStates', next);
    this.filterState.requestApplyFilters();
  }

  clearAll(): void {
    this.filterState.clearAppliedDimension('state');
    this.filterState.requestApplyFilters();
  }
}

// Register state picker on module load.
pickerRegistry['state'] = {
  component: StateGridPickerComponent,
  label: 'States',
  field: 'selectedStates',
  group: 'Geography',
};
